import { useState, useEffect } from 'react'

interface ReauthModalProps {
  isOpen: boolean
  onReauth: (token: string) => void
  onCancel: () => void
  errorMessage?: string
}

export default function ReauthModal({ isOpen, onReauth, errorMessage }: ReauthModalProps) {
  const [token, setToken] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Handle Escape key - logout instead of cancel
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleLogout()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token.trim()) return

    setIsSubmitting(true)
    try {
      await onReauth(token.trim())
      setToken('')
    } catch (error) {
      console.error('Reauth failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLogout = () => {
    sessionStorage.clear()
    window.location.href = '/admin'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-md flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full border-l-4 border-amber-500 animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <span className="text-2xl">🔑</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Session Expired
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please re-enter your token to continue
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">
                {errorMessage}
              </p>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="reauth-token" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              GCP Identity Token
            </label>
            <textarea
              id="reauth-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-xs resize-none"
              rows={6}
              placeholder="Paste your GCP identity token here..."
              disabled={isSubmitting}
              autoFocus
              style={{
                WebkitTextSecurity: 'disc',
                textSecurity: 'disc'
              } as React.CSSProperties}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Run: <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">gcloud auth print-identity-token</code>
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              💡 Your work is safe - this won't reload the page
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleLogout}
              className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors font-medium"
              disabled={isSubmitting}
            >
              Logout
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors font-medium"
              disabled={isSubmitting || !token.trim()}
            >
              {isSubmitting ? 'Authenticating...' : 'Continue'}
            </button>
          </div>
        </form>

        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Press <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Escape</kbd> to logout
          </p>
        </div>
      </div>
    </div>
  )
}
